import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { MuiMarkdown } from 'mui-markdown'
import { sortBy } from 'ramda'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { MotherIcon } from 'src/v3/core/icons'
import {
  createLikertAnalysis,
  createCourseLikertAnalysis,
  fetchEventLikertAnalyses,
  fetchCourseLikertAnalyses,
  pollLikertAnalysisStatus,
  pollCourseLikertAnalysisStatus,
  selectActiveLikertPolling,
  selectEventLikertAnalysesByEventID,
  selectCourseLikertAnalysesByCourseID,
  selectLikertAnalysisStatusByEventID,
  selectCourseLikertAnalysisStatusByCourseID,
  selectLikertPollingProgress,
  EventLikertAnalysis,
  CourseLikertAnalysis,
} from 'src/v3/content-pipeline/performance.slice'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'
import MotherThinking from 'src/v3/components/mother-thinking'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'

type LikertAnalysisSectionProps = {
  domain: 'live' | 'enduring'
  itemID: string
  licenseGroup: string
  startDate: string
  endDate: string
}

type AnalysisItem = EventLikertAnalysis | CourseLikertAnalysis

let PINS_STORAGE_KEY = 'likert-analysis-pinned'

let getPinnedIds = (): string[] => {
  try {
    let stored = localStorage.getItem(PINS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

let togglePin = (id: string): string[] => {
  let pins = getPinnedIds()
  let updated = pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id]
  localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(updated))
  return updated
}

let getThinkingMessage = (progress: any[]): string => {
  if (progress.length === 0) return 'Computing Likert averages...'
  let last = progress[progress.length - 1]
  return last.message || 'Analyzing ratings...'
}

let capitalizeLicenseGroup = (group: string): string => {
  let groupMap: Record<string, string> = {
    paramedic: 'Paramedic',
    emt: 'EMT',
    aemt: 'AEMT',
    nurse: 'Nurse',
    other: 'Other',
    all: 'All',
  }
  return groupMap[group.toLowerCase()] || group
}

let formatAnalysisLabel = (analysis: AnalysisItem): string => {
  if (analysis.name) return analysis.name
  let start = analysis.startDate
    ? new Date(analysis.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : ''
  let end = analysis.endDate
    ? new Date(analysis.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : ''
  return start && end ? `${start} - ${end}` : 'Unnamed analysis'
}

let AnalysisList = ({
  analyses,
  selectedId,
  pinnedIds,
  onSelect,
  onTogglePin,
}: {
  analyses: AnalysisItem[]
  selectedId: string
  pinnedIds: string[]
  onSelect: (id: string) => void
  onTogglePin: (id: string) => void
}) => {
  let sorted = sortBy((a: AnalysisItem) => {
    let pinned = pinnedIds.includes(a.id) ? 0 : 1
    let time = a.createdTime ? -new Date(a.createdTime).getTime() : 0
    return pinned * 1e15 + time
  }, analyses)

  return (
    <Box
      sx={{
        maxHeight: 320,
        overflowY: 'auto',
        borderRadius: 1.5,
        bgcolor: 'background.paperDark',
        border: '1px solid',
        borderColor: 'divider',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
      }}
    >
      {sorted.map((analysis, index) => {
        let isSelected = analysis.id === selectedId
        let isPinned = pinnedIds.includes(analysis.id)
        let statusColor =
          analysis.status === 'SessionCompleted'
            ? 'success.main'
            : analysis.status === 'SessionFailed'
              ? 'error.main'
              : 'warning.main'
        let statusLabel =
          analysis.status === 'SessionCompleted'
            ? 'Completed'
            : analysis.status === 'SessionFailed'
              ? 'Failed'
              : 'In Progress'
        let createdLabel = analysis.createdTime
          ? new Date(analysis.createdTime).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })
          : ''

        return (
          <Box
            key={analysis.id}
            onClick={() => onSelect(analysis.id)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              borderLeft: '3px solid',
              borderLeftColor: isSelected ? 'primary.main' : 'transparent',
              borderBottom: index < sorted.length - 1 ? '1px solid' : 'none',
              borderBottomColor: 'divider',
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: isSelected
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(255, 255, 255, 0.03)',
              },
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusColor,
                flexShrink: 0,
                mt: 0.75,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatAnalysisLabel(analysis)}
                </Typography>
                <Chip
                  label={capitalizeLicenseGroup(analysis.licenseGroup)}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}
              >
                {createdLabel && (
                  <Typography
                    color="text.disabled"
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                    }}
                  >
                    {createdLabel}
                  </Typography>
                )}
                <Typography
                  color="text.disabled"
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                  }}
                >
                  · {statusLabel}
                </Typography>
              </Box>
            </Box>
            <Tooltip title={isPinned ? 'Unpin' : 'Pin'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onTogglePin(analysis.id)
                }}
                sx={{
                  mt: 0.25,
                  color: isPinned ? 'primary.main' : 'text.disabled',
                  opacity: isPinned ? 1 : 0.5,
                  '&:hover': { opacity: 1 },
                }}
              >
                {isPinned ? (
                  <PushPinIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )
      })}
    </Box>
  )
}

let AnalysisMetadataBar = ({
  analysis,
  isPinned,
  onTogglePin,
}: {
  analysis: AnalysisItem
  isPinned: boolean
  onTogglePin: () => void
}) => {
  let startLabel = analysis.startDate
    ? new Date(analysis.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'
  let endLabel = analysis.endDate
    ? new Date(analysis.endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'
  let createdLabel = analysis.createdTime
    ? new Date(analysis.createdTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown'

  let statusLabel =
    analysis.status === 'SessionCompleted'
      ? 'Completed'
      : analysis.status === 'SessionFailed'
        ? 'Failed'
        : 'In Progress'
  let statusColor =
    analysis.status === 'SessionCompleted'
      ? 'success'
      : analysis.status === 'SessionFailed'
        ? 'error'
        : ('warning' as 'success' | 'error' | 'warning')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mb: 2,
      }}
    >
      <Typography color="text.secondary" variant="body2">
        Range:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {startLabel} - {endLabel}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        License:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {capitalizeLicenseGroup(analysis.licenseGroup)}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Created:{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {createdLabel}
        </Typography>
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Status:{' '}
        <Chip
          label={statusLabel}
          size="small"
          variant="outlined"
          sx={{
            color: statusColor,
            height: 22,
            fontSize: '0.75rem',
          }}
        />
      </Typography>
      <Box sx={{ ml: 'auto' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isPinned ? (
              <PushPinIcon sx={{ fontSize: 16 }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={onTogglePin}
        >
          {isPinned ? 'Pinned' : 'Pin'}
        </Button>
      </Box>
    </Box>
  )
}

export let LikertAnalysisSection = ({
  domain,
  itemID,
  licenseGroup,
  startDate,
  endDate,
}: LikertAnalysisSectionProps) => {
  let dispatch = useAppDispatch()
  let eventAnalyses = useAppSelector(selectEventLikertAnalysesByEventID(itemID))
  let courseAnalyses = useAppSelector(
    selectCourseLikertAnalysesByCourseID(itemID),
  )
  let eventStatus = useAppSelector(selectLikertAnalysisStatusByEventID(itemID))
  let courseStatus = useAppSelector(
    selectCourseLikertAnalysisStatusByCourseID(itemID),
  )
  let activePolling = useAppSelector(selectActiveLikertPolling)
  let pollingProgress = useAppSelector(selectLikertPollingProgress)

  let analyses = domain === 'live' ? eventAnalyses : courseAnalyses
  let status = domain === 'live' ? eventStatus : courseStatus

  let [selectedAnalysisID, setSelectedAnalysisID] = useState<string>('')
  let [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null)
  let [isExpanded, setIsExpanded] = useState(false)
  let [pinnedIds, setPinnedIds] = useState<string[]>(getPinnedIds)
  let pollingInterval = useRef<number | undefined>(undefined)

  let selectedAnalysis = analyses?.find((a) => a.id === selectedAnalysisID)
  let isPolling = !!(
    activePolling &&
    ((domain === 'live' && activePolling.eventID === itemID) ||
      (domain === 'enduring' && activePolling.courseID === itemID))
  )
  let isLoading = status === 'pending'

  useEffect(() => {
    setSelectedAnalysisID('')
    if (domain === 'live') {
      dispatch(fetchEventLikertAnalyses(itemID))
    } else {
      dispatch(fetchCourseLikertAnalyses(itemID))
    }
  }, [dispatch, itemID, domain])

  useEffect(() => {
    if (analyses && analyses.length > 0 && !selectedAnalysisID) {
      let completed = analyses.filter((a) => a.status === 'SessionCompleted')
      if (completed.length > 0) {
        let sorted = sortBy(
          (a) => -(a.createdTime ? new Date(a.createdTime).getTime() : 0),
          completed,
        )
        setSelectedAnalysisID(sorted[0].id)
      } else {
        setSelectedAnalysisID(analyses[0].id)
      }
    }
  }, [analyses, selectedAnalysisID])

  useEffect(() => {
    if (
      selectedAnalysis &&
      !selectedAnalysis.result &&
      selectedAnalysis.status === 'SessionCompleted'
    ) {
      setFetchingResultFor(selectedAnalysis.correlationID)
      let pollFn =
        domain === 'live'
          ? pollLikertAnalysisStatus
          : pollCourseLikertAnalysisStatus
      dispatch(pollFn(selectedAnalysis.correlationID)).finally(() => {
        setFetchingResultFor(null)
      })
    }
  }, [selectedAnalysis, dispatch, domain])

  useEffect(() => {
    setIsExpanded(false)
  }, [selectedAnalysisID])

  useEffect(() => {
    let isActiveForThisItem =
      (domain === 'live' && activePolling?.eventID === itemID) ||
      (domain === 'enduring' && activePolling?.courseID === itemID)

    if (activePolling && isActiveForThisItem) {
      let pollFn =
        domain === 'live'
          ? pollLikertAnalysisStatus
          : pollCourseLikertAnalysisStatus

      let pollStatus = async () => {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        let result = await dispatch(pollFn(activePolling.correlationID))
        if (result.payload) {
          let payload = result.payload as any
          if (
            payload.status === 'SessionCompleted' ||
            payload.status === 'SessionFailed'
          ) {
            clearInterval(pollingInterval.current)
            if (payload.status === 'SessionCompleted') {
              if (domain === 'live') {
                dispatch(fetchEventLikertAnalyses(itemID))
              } else {
                dispatch(fetchCourseLikertAnalyses(itemID))
              }
            }
          }
        }
      }

      pollStatus()
      // eslint-disable-next-line fp/no-mutation
      pollingInterval.current = window.setInterval(pollStatus, 3000)
    }

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current)
      }
    }
  }, [activePolling, dispatch, itemID, domain])

  let handleSubmitAnalysis = () => {
    let start = new Date(startDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    let end = new Date(endDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    let name = `${capitalizeLicenseGroup(licenseGroup)}: ${start} - ${end}`
    if (domain === 'live') {
      dispatch(
        createLikertAnalysis({
          eventID: itemID,
          licenseGroup,
          startDate,
          endDate,
          name,
        }),
      )
    } else {
      dispatch(
        createCourseLikertAnalysis({
          courseID: itemID,
          licenseGroup,
          startDate,
          endDate,
          name,
        }),
      )
    }
  }

  let handleTogglePin = (id: string) => {
    let updated = togglePin(id)
    setPinnedIds(updated)
  }

  let headerJSX = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
            }}
          >
            Likert Averages
          </Typography>
          <Chip
            icon={<MotherIcon />}
            label="MOTHER · Structured"
            size="small"
            variant="outlined"
          />
        </Box>
        <Typography color="text.secondary" variant="body2">
          Per-question averages and overall rating from structured survey
          responses.
        </Typography>
      </Box>
      <Button
        variant="contained"
        startIcon={<MotherIcon />}
        onClick={handleSubmitAnalysis}
        disabled={isPolling}
      >
        + Compute New Timespan
      </Button>
    </Box>
  )

  if (!analyses || analyses.length === 0) {
    if (isLoading) {
      return (
        <Card variant="outlined" sx={{ p: 3 }}>
          {headerJSX}
          <Skeleton
            variant="rectangular"
            sx={{
              height: 60,
            }}
          />
          <Skeleton variant="text" sx={{ mt: 2 }} />
          <Skeleton variant="text" />
        </Card>
      )
    }

    return (
      <Card variant="outlined" sx={{ p: 3 }}>
        {headerJSX}
        {!isPolling && (
          <Typography color="text.secondary" variant="body2">
            No analyses yet. Compute a timespan to get per-question Likert
            averages from structured survey responses.
          </Typography>
        )}
        {isPolling && (
          <MotherThinking
            isThinking={true}
            currentMessage={getThinkingMessage(pollingProgress)}
            size="medium"
          />
        )}
      </Card>
    )
  }

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      {headerJSX}
      <AnalysisList
        analyses={analyses}
        selectedId={selectedAnalysisID}
        pinnedIds={pinnedIds}
        onSelect={setSelectedAnalysisID}
        onTogglePin={handleTogglePin}
      />
      {isPolling && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <MotherThinking
            isThinking={true}
            currentMessage={getThinkingMessage(pollingProgress)}
            size="medium"
          />
        </Box>
      )}
      {!isPolling && selectedAnalysis && (
        <Box sx={{ mt: 2 }}>
          <AnalysisMetadataBar
            analysis={selectedAnalysis}
            isPinned={pinnedIds.includes(selectedAnalysis.id)}
            onTogglePin={() => handleTogglePin(selectedAnalysis.id)}
          />

          <Box sx={markdownStyles}>
            {selectedAnalysis.result ? (
              <Box sx={{ position: 'relative' }}>
                <div
                  className={
                    isExpanded ? 'max-h-none' : 'max-h-[400px] overflow-hidden'
                  }
                >
                  <MuiMarkdown>{selectedAnalysis.result}</MuiMarkdown>
                </div>

                {!isExpanded && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, transparent, #1e2124)',
                    }}
                  />
                )}

                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{ mt: 2, width: '100%' }}
                  variant="text"
                >
                  {isExpanded ? 'Show Less' : 'Show More'}
                </Button>
              </Box>
            ) : selectedAnalysis.status === 'SessionFailed' ||
              selectedAnalysis.status === 'Failed' ? (
              <Box>
                <Typography color="error.main" variant="body2">
                  This analysis failed to complete.
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                  }}
                >
                  Correlation ID: {selectedAnalysis.correlationID}
                </Typography>
              </Box>
            ) : selectedAnalysis.status === 'Pending' ||
              selectedAnalysis.status === 'Running' ||
              fetchingResultFor === selectedAnalysis.correlationID ? (
              <Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: '100%',
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: '100%',
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: '80%',
                    height: 30,
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      )}
    </Card>
  )
}
